/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { uniqueId } from 'lodash-es';

@Component({
  selector: 'ods-slide-toggle',
  host: { class: 'inline-block' },
  template: `
    <label
      [attr.for]="inputId"
      class="text-text inline-flex items-center gap-3 text-sm select-none"
      [class.cursor-not-allowed]="disabled"
      [class.cursor-pointer]="!disabled"
      [class.opacity-80]="disabled"
    >
      <span class="relative inline-block">
        <input
          type="checkbox"
          role="switch"
          class="peer absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none opacity-0"
          [id]="inputId"
          [checked]="_checked"
          [disabled]="disabled || null"
          [attr.aria-label]="label"
          (change)="onToggle($event)"
          (keydown.enter)="onEnterKey($event)"
        />
        <span
          class="bg-ozggray-300 peer-checked:bg-primary peer-focus-visible:outline-focus dark:bg-ozggray-800 dark:peer-checked:bg-primary block h-6 w-11 rounded-full outline-2 outline-offset-2 outline-transparent transition-colors duration-200 outline-solid peer-disabled:opacity-80"
        ></span>
        <span
          class="bg-whitetext pointer-events-none absolute top-0.5 left-0.5 flex size-5 items-center justify-center rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5"
        >
          <svg
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            class="text-primary size-3"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
            [class.hidden]="!_checked"
          >
            <path d="M5 12l5 5L20 7" />
          </svg>
          <svg
            viewBox="0 0 10 2"
            xmlns="http://www.w3.org/2000/svg"
            class="fill-ozggray-800 dark:fill-ozggray-700 w-2.5"
            aria-hidden="true"
            [class.hidden]="_checked"
          >
            <rect width="10" height="2" rx="1" />
          </svg>
        </span>
      </span>
      {{ label }}
    </label>
  `,
})
export class SlideToggleComponent {
  @Input() set checked(value: boolean) {
    this._checked = value;
  }

  @Input() label: string = '';
  @Input() disabled: boolean = false;

  @Output() valueChanged: EventEmitter<boolean> = new EventEmitter();

  readonly inputId: string = uniqueId('slide-toggle-');
  _checked: boolean = false;

  onToggle(event: Event): void {
    this._checked = (event.target as HTMLInputElement).checked;
    this.valueChanged.emit(this._checked);
  }

  onEnterKey(event: KeyboardEvent): void {
    event.preventDefault();
    this._checked = !this._checked;
    this.valueChanged.emit(this._checked);
  }
}
